import React, { useState } from 'react';
import { Modal } from '../../common/Modal';
import { Bed, Building2, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { roomNumber: number; roomType: 'd' | 't' | 'q' }) => void;
};

export const AddRoomModal = ({ isOpen, onClose, onSubmit }: Props) => {
  const { translations } = useLanguage();
  const [roomNumber, setRoomNumber] = useState<string>('');
  const [roomType, setRoomType] = useState<'d' | 't' | 'q'>('d');
  const [errors, setErrors] = useState<{ roomNumber?: string }>({});

  // Room Type options
  const roomTypes = [
    { value: 'd', label: translations?.roomTypes?.double || 'Double', capacity: 2 },
    { value: 't', label: translations?.roomTypes?.triple || 'Triple', capacity: 3 },
    { value: 'q', label: translations?.roomTypes?.quadruple || 'Quadruple', capacity: 4 },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate room number
    const newErrors: { roomNumber?: string } = {};
    
    if (!roomNumber) {
      newErrors.roomNumber = translations?.addRoomModal?.roomNumberRequired || 'Room number is required';
    } else if (!/^\d+$/.test(roomNumber)) {
      newErrors.roomNumber = translations?.addRoomModal?.roomNumberInvalid || 'Room number must be a number';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      onSubmit({
        roomNumber: parseInt(roomNumber),
        roomType
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={translations?.addRoomModal?.title || 'Add New Room'}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Room Number */}
        <div>
          <label htmlFor="roomNumber" className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.addRoomModal?.roomNumber || 'Room Number'}
            <span className="text-red-500">*</span>
          </label>
          <input
            id="roomNumber"
            type="text"
            value={roomNumber}
            onChange={(e) => setRoomNumber(e.target.value)}
            placeholder={translations?.addRoomModal?.enterRoomNumber || 'Enter room number'}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.roomNumber ? 'border-red-500' : ''
            }`}
          />
          {errors.roomNumber && (
            <p className="mt-1 text-sm text-red-600">{errors.roomNumber}</p>
          )}
        </div>

        {/* Room Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {translations?.addRoomModal?.roomType || 'Room Type'}
          </label>
          
          <div className="grid grid-cols-3 gap-3">
            {roomTypes.map((type) => (
              <motion.div
                key={type.value}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setRoomType(type.value as 'd' | 't' | 'q')}
                className={`
                  p-3 rounded-lg border-2 transition-all cursor-pointer
                  ${roomType === type.value
                    ? 'border-primary-500 bg-primary-50 shadow-sm'
                    : 'border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className={`
                    p-2 rounded-full transition-colors
                    ${roomType === type.value ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'}
                  `}>
                    <Bed size={18} />
                  </div>
                  <div className="font-medium">{type.label}</div>
                  <div className="text-sm text-gray-500">
                    {type.capacity} {translations?.addRoomModal?.beds || 'beds'}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 mr-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.common?.cancel || 'Cancel'}
          </button>
          <button
            type="submit"
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            <Plus size={18} />
            <span>{translations?.addRoomModal?.addRoom || 'Add Room'}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};